import { useEffect, useMemo, useRef, useState } from "react";
import { t } from "ttag";

import ErrorBoundary from "metabase/ErrorBoundary";
import { useListUserRecipientsQuery } from "metabase/api";
import { SidesheetCard } from "metabase/common/components/Sidesheet";
import { useReportMetadata } from "metabase/jerry/Metadata/useReportMetadata";
import { Loader, MultiSelect } from "metabase/ui";
import { DefaultSelectItem } from "metabase/ui/components/inputs/Select";

interface ReportExtendMetadataProps {
  reportId: number;
  reportType: "question" | "dashboard";
  reportName: string;
  creatorName?: string;
  creatorId?: number;
  onCategoriesChange?: (categories: string[]) => void;
  canWrite: boolean;
}

const DEFAULT_CATEGORY_OPTIONS = [
  "Stats email",
  "Monitor",
  "Core",
  "Archived",
  "Immutable",
  "Clickhouse25",
] as const;

const CREATE_CATEGORY_VALUE = "__metabase_create_category__";

const ReportExtendMetadataInner = ({
  reportId,
  reportType,
  reportName,
  creatorName,
  creatorId,
  onCategoriesChange,
  canWrite,
}: ReportExtendMetadataProps) => {
  const { metadata, isLoading, updateMetadata } = useReportMetadata(
    reportId,
    reportType,
    reportName,
    creatorName,
    creatorId,
  );

  const { data: usersData, error: usersError } = useListUserRecipientsQuery();
  const [categoryOptions, setCategoryOptions] = useState<string[]>([
    ...DEFAULT_CATEGORY_OPTIONS,
  ]);
  const [searchValue, setSearchValue] = useState("");
  const pendingCreateValueRef = useRef("");

  const isWritable = canWrite && !!usersData && !usersError;

  const userOptions = useMemo(() => {
    const options =
      usersData?.data?.map((user) => ({
        label: user.common_name || user.first_name + " " + user.last_name,
        value: user.common_name || user.first_name + " " + user.last_name,
      })) || [];

    const uniqueOptionsMap = new Map();
    options.forEach((option) => {
      if (!uniqueOptionsMap.has(option.value)) {
        uniqueOptionsMap.set(option.value, option);
      }
    });

    return Array.from(uniqueOptionsMap.values());
  }, [usersData]);

  const normalizedOptionSet = useMemo(() => {
    return new Set(categoryOptions.map((option) => option.toLowerCase()));
  }, [categoryOptions]);

  const trimmedSearchValue = searchValue.trim();
  const canCreateCategoryOption =
    trimmedSearchValue.length > 0 &&
    !normalizedOptionSet.has(trimmedSearchValue.toLowerCase());

  const multiSelectData = useMemo(() => {
    const options = categoryOptions.map((category) => ({
      value: category,
      label: category,
    }));

    if (canCreateCategoryOption && isWritable) {
      options.unshift({
        value: CREATE_CATEGORY_VALUE,
        label: trimmedSearchValue,
      });
    }

    return options;
  }, [
    categoryOptions,
    canCreateCategoryOption,
    trimmedSearchValue,
    isWritable,
  ]);

  const handleSave = async (updatedValues: Partial<typeof metadata>) => {
    const newMetadata = { ...metadata, ...updatedValues };
    await updateMetadata(newMetadata);
    if (updatedValues.categories && onCategoriesChange) {
      onCategoriesChange(updatedValues.categories);
    }
  };

  useEffect(() => {
    if (!metadata.categories?.length) {
      return;
    }

    setCategoryOptions((current) => {
      const optionsSet = new Set(current);
      metadata.categories?.forEach((category) => optionsSet.add(category));
      return Array.from(optionsSet);
    });
  }, [metadata.categories]);

  const handleCategoryCreate = async (value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return;
    }

    setCategoryOptions((current) => {
      const hasOption = current.some(
        (option) => option.toLowerCase() === trimmedValue.toLowerCase(),
      );
      return hasOption ? current : [...current, trimmedValue];
    });

    const currentCategories = metadata.categories || [];
    if (!currentCategories.includes(trimmedValue)) {
      await handleSave({ categories: [...currentCategories, trimmedValue] });
    }

    setSearchValue("");
    pendingCreateValueRef.current = "";
  };

  const handleCategoriesChange = (values: string[]) => {
    if (values.includes(CREATE_CATEGORY_VALUE)) {
      const valueToCreate = pendingCreateValueRef.current || trimmedSearchValue;
      void handleCategoryCreate(valueToCreate);
      return;
    }

    void handleSave({ categories: values });
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    const trimmedValue = value.trim();
    if (trimmedValue) {
      pendingCreateValueRef.current = trimmedValue;
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <SidesheetCard>
      <MultiSelect
        label={t`Category`}
        data={multiSelectData}
        value={metadata.categories || []}
        onChange={handleCategoriesChange}
        searchable
        clearable
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        disabled={!isWritable}
        renderOption={(item) => {
          if (item.option.value === CREATE_CATEGORY_VALUE) {
            return (
              <DefaultSelectItem
                {...item.option}
                icon="add"
                selected={false}
                label={
                  <>
                    {t`Add`} {`"${trimmedSearchValue}"`}
                  </>
                }
              />
            );
          }

          return <DefaultSelectItem {...item.option} selected={item.checked} />;
        }}
      />
      <MultiSelect
        label={t`Maintainer`}
        data={userOptions}
        value={metadata.maintainer || []}
        onChange={(value) => handleSave({ maintainer: value })}
        searchable
        clearable
        disabled={!isWritable}
      />
    </SidesheetCard>
  );
};

export const ReportExtendMetadata = (props: ReportExtendMetadataProps) => (
  <ErrorBoundary errorComponent={() => null}>
    <ReportExtendMetadataInner {...props} />
  </ErrorBoundary>
);
